/**
 * 赠品规则计算引擎
 *
 * 算法核心：动态规划求解最小赠品数
 * - dp[i] = 购买 i 件商品时，能得到的 最小赠品数
 * - 状态转移：dp[i] = min(dp[i], dp[i - buy_qty] + gift_qty)
 *
 * 商家利益最大化：在满足规则的前提下，选择赠品最少的组合
 */

export class GiftRuleEngine {
  constructor(rules = []) {
    // 规则按 buy_quantity 升序排序，方便处理
    this.rules = this._normalizeRules(rules)
  }

  /**
   * 标准化规则数组
   * 输入可能是旧格式 {buy_quantity, gift_quantity} 或新格式 [{buy_qty, gift_qty}, ...]
   */
  _normalizeRules(rules) {
    if (!Array.isArray(rules)) {
      // 兼容旧格式：单条规则对象
      if (rules && typeof rules === 'object') {
        const buyQty = rules.buy_quantity || rules.buy_qty || 0
        const giftQty = rules.gift_quantity || rules.gift_qty || 0
        if (buyQty > 0 && giftQty >= 0) {
          return [{ buy_qty: buyQty, gift_qty: giftQty }]
        }
      }
      return []
    }

    return rules
      .map(rule => ({
        buy_qty: parseInt(rule.buy_qty || rule.buy_quantity || 0, 10),
        gift_qty: parseInt(rule.gift_qty || rule.gift_quantity || 0, 10)
      }))
      .filter(rule => rule.buy_qty > 0 && rule.gift_qty >= 0)
      .sort((a, b) => a.buy_qty - b.buy_qty)
  }

  /**
   * 计算最优赠品数量（商家利益最大化 = 赠品最小化）
   *
   * @param {number} purchaseQty - 购买数量
   * @returns {object} { total_gifts, combination, explanation, remainder }
   *   - total_gifts: 总赠品数
   *   - combination: 使用的规则组合数组 [{rule, times, subtotal_buy, subtotal_gift}, ...]
   *   - explanation: 人类可读的解释文字
   *   - remainder: 剩余不计入赠品的购买数量
   */
  calculate(purchaseQty) {
    const qty = parseInt(purchaseQty, 10)
    if (!qty || qty <= 0 || this.rules.length === 0) {
      return {
        total_gifts: 0,
        combination: [],
        explanation: '未达到赠品条件',
        remainder: qty || 0
      }
    }

    // 使用动态规划计算最小赠品
    const result = this._dpCalculate(qty)
    return result
  }

  /**
   * 动态规划核心算法
   * dp[i] = 购买 i 件商品时，能得到的 最小赠品数
   * 同时记录路径用于回溯
   */
  _dpCalculate(qty) {
    // 初始化 DP 数组
    // dp[i] 表示购买 i 件商品时的最小赠品数
    const dp = new Array(qty + 1).fill(Infinity)
    const path = new Array(qty + 1).fill(null) // 记录用了哪条规则

    dp[0] = 0 // 买 0 件，得 0 个赠品

    // 状态转移
    // 对于每个购买数量 i，尝试使用每条规则
    for (let i = 1; i <= qty; i++) {
      for (const rule of this.rules) {
        if (rule.buy_qty <= i) {
          const prevGifts = dp[i - rule.buy_qty]
          if (prevGifts !== Infinity) {
            const newGifts = prevGifts + rule.gift_qty
            // 商家利益最大化：选择赠品最少的方案
            if (newGifts < dp[i]) {
              dp[i] = newGifts
              path[i] = rule
            }
          }
        }
      }
    }

    // 找到最佳方案
    // 从 qty 往下找，找到第一个可达的（赠品数有限的）数量
    let bestQty = 0
    let bestGifts = 0

    for (let i = qty; i >= 0; i--) {
      if (dp[i] !== Infinity) {
        bestQty = i
        bestGifts = dp[i]
        break
      }
    }

    // 回溯路径，构建组合
    const combination = this._backtrackPath(path, bestQty)
    const remainder = qty - bestQty

    return {
      total_gifts: bestGifts,
      combination: combination,
      remainder: remainder,
      explanation: this._generateExplanation(combination, bestGifts, remainder, qty)
    }
  }

  /**
   * 回溯路径，构建使用的规则组合
   */
  _backtrackPath(path, totalQty) {
    const combination = []
    let remaining = totalQty

    while (remaining > 0 && path[remaining]) {
      const rule = path[remaining]
      // 查找是否已有相同规则的记录
      const existing = combination.find(c => c.rule.buy_qty === rule.buy_qty)

      if (existing) {
        existing.times++
        existing.subtotal_buy += rule.buy_qty
        existing.subtotal_gift += rule.gift_qty
      } else {
        combination.push({
          rule: rule,
          times: 1,
          subtotal_buy: rule.buy_qty,
          subtotal_gift: rule.gift_qty
        })
      }

      remaining -= rule.buy_qty
    }

    // 按 buy_qty 降序排列，大的规则在前
    return combination.sort((a, b) => b.rule.buy_qty - a.rule.buy_qty)
  }

  /**
   * 生成人类可读的解释文字
   */
  _generateExplanation(combination, totalGifts, remainder, originalQty) {
    if (combination.length === 0) {
      return '未达到赠品条件'
    }

    const parts = combination.map(c => {
      if (c.times === 1) {
        return `满${c.rule.buy_qty}赠${c.rule.gift_qty}`
      }
      return `满${c.rule.buy_qty}赠${c.rule.gift_qty} × ${c.times}次`
    })

    let explanation = `购买${originalQty}件：` + parts.join('，')
    explanation += `，共赠${totalGifts}件`

    if (remainder > 0) {
      explanation += `（${remainder}件不计入满赠）`
    }

    return explanation
  }

  /**
   * 批量计算多个商品的赠品（用于购物车场景）
   *
   * @param {Array} items - [{id, name, quantity, applicable_rules}, ...]
   * @returns {Array} 每个商品的赠品计算结果
   */
  calculateBatch(items) {
    return items.map(item => ({
      ...item,
      gift_result: this.calculate(item.quantity)
    }))
  }

  /**
   * 验证规则是否合法
   */
  static validateRules(rules) {
    const errors = []

    if (!Array.isArray(rules) || rules.length === 0) {
      errors.push('至少需要一条赠品规则')
      return errors
    }

    rules.forEach((rule, index) => {
      const buyQty = parseInt(rule.buy_qty || rule.buy_quantity, 10)
      const giftQty = parseInt(rule.gift_qty || rule.gift_quantity, 10)

      if (isNaN(buyQty) || buyQty <= 0) {
        errors.push(`规则${index + 1}：购买数量必须是正整数`)
      }
      if (isNaN(giftQty) || giftQty < 0) {
        errors.push(`规则${index + 1}：赠品数量必须是非负整数`)
      }
      if (buyQty <= giftQty) {
        errors.push(`规则${index + 1}：购买数量(${buyQty})应大于赠品数量(${giftQty})，否则商家亏损`)
      }
    })

    // 检查规则是否有重叠或矛盾
    const sortedRules = [...rules].sort((a, b) => {
      const aBuy = parseInt(a.buy_qty || a.buy_quantity, 10)
      const bBuy = parseInt(b.buy_qty || b.buy_quantity, 10)
      return aBuy - bBuy
    })

    for (let i = 1; i < sortedRules.length; i++) {
      const prev = sortedRules[i - 1]
      const curr = sortedRules[i]
      const prevBuy = parseInt(prev.buy_qty || prev.buy_quantity, 10)
      const prevGift = parseInt(prev.gift_qty || prev.gift_quantity, 10)
      const currBuy = parseInt(curr.buy_qty || curr.buy_quantity, 10)
      const currGift = parseInt(curr.gift_qty || curr.gift_quantity, 10)

      // 检查收益率是否递减（买得越多，单位赠品应越少或持平）
      const prevRatio = prevGift / prevBuy
      const currRatio = currGift / currBuy
      if (currRatio > prevRatio) {
        errors.push(`规则${i + 1}：买${currBuy}赠${currGift}的收益率(${currRatio.toFixed(2)})高于低档规则，建议调整`)
      }
    }

    return errors
  }

  /**
   * 生成规则预览数据（用于展示各档位的赠品效果）
   *
   * @param {number} maxQty - 最大展示数量
   * @returns {Array} [{purchase_qty, gift_qty, explanation}, ...]
   */
  generatePreview(maxQty = 50) {
    const preview = []
    for (let i = 1; i <= maxQty; i++) {
      const result = this.calculate(i)
      preview.push({
        purchase_qty: i,
        gift_qty: result.total_gifts,
        remainder: result.remainder,
        explanation: result.explanation
      })
    }
    return preview
  }
}

/**
 * 便捷函数：计算单次购买赠品
 */
export function calculateGift(rules, purchaseQty) {
  const engine = new GiftRuleEngine(rules)
  return engine.calculate(purchaseQty)
}

/**
 * 便捷函数：验证规则
 */
export function validateGiftRules(rules) {
  return GiftRuleEngine.validateRules(rules)
}

export default GiftRuleEngine
